import { createSelector } from 'reselect'
import { calculateTotalDiscount, convert2ExtraDiscountItem, convert2MarketingCouponItem } from '../../../component/Restaurant/areas/tables/modalPaymentType/helper'
import { calcCartItemTotalPrice, clone, getPercentDiscountInCurrency, roundPrice } from '../../../helper'
import { selectUsername } from '../system'
import { selectTimerCounter } from '../timerCounter'
import { addMinutes } from 'date-fns'
import { selectFlyerCouponError } from '../coupon'
import { logger } from '../../../logger/logger'
import { calcItemTotalPrice } from '../../reducer/tableCart/helper'

export const selectRestaurantOrdersTable = store => store.restaurant.table?.orders
export const selectRestaurantSplitsTable = store => store.restaurant.table?.splits

export const selectGiftCouponInOrders = createSelector(
	selectRestaurantOrdersTable,
	(orders) => {
		if (orders?.length) {
			return !!orders[0].items?.find(item => item.type === 'GIFT_COUPON') || null
		}
		return null
	},
)

export const selectGiftCouponInSplit = createSelector(
	selectRestaurantSplitsTable,
	(splits) => {
		if (splits?.length) {
			return !!splits[0].items?.find(item => item.type === 'GIFT_COUPON') || null
		}
		return null
	},
)

export const selectGiftCouponOnTable = createSelector(
	selectGiftCouponInOrders,
	selectGiftCouponInSplit,
	(hasGiftCouponInOrders, hasGiftCouponInSplits) => {
		return hasGiftCouponInOrders || hasGiftCouponInSplits
	},
)

const selectRegisteredOrders = store => store.tableCart.orders
const selectRegisteredSplits = store => store.tableCart.splits
const selectNewOrder = store => store.tableCart.newOrder
const selectCartDiscount = store => store.cartDiscount?.discount
export const selectCancelItems = store => store.tableCart.cancelItems
export const selectCancelSubItems = store => store.tableCart.cancelSubItems
const selectCancellationDetailsItem = store => store.tableCart.cancellationDetailsItem

export const selectCartDiscountValue = (hasGiftCoupon) => createSelector(
	selectCartDiscount,
	(discount) => {
		return hasGiftCoupon ? 0 : discount
	},
)

export const selectRegisteredOrdersWithCancellationData = createSelector(
	[selectRegisteredOrders, selectCancelItems, selectCancelSubItems],
	(orders, cancelItems, cancelSubItems) => {
		const copyOrders = clone(orders)

		if (!cancelItems?.length && !cancelSubItems?.length) {
			return copyOrders
		}

		// Process items marked for cancellation
		cancelItems.forEach(cancelItem => {
			const { orderId, itemPositionId } = cancelItem

			const order = copyOrders.find(order => order.id === orderId)
			if (!order) return

			const item = order.items.find(item => item.itemPositionId === itemPositionId)
			if (!item || item.status === 'CANCELED') return

			item.cancellationMetaData = { ...cancelItem }

			if (item.status !== 'CANCELED') {
				item.status = 'PREPARED_TO_CANCEL'
			}

			item.items = item.items?.map(subItem =>
				subItem.status !== 'CANCELED'
					? { ...subItem, status: 'PREPARED_TO_CANCEL' }
					: subItem
			)

		})

		// Process subitems marked for cancellation
		cancelSubItems.forEach(cancelSubItem => {
			const { orderId, itemPositionId, subItemPositionId } = cancelSubItem

			const order = copyOrders.find(order => order.id === orderId)
			if (!order) return

			const item = order.items.find(item => item.itemPositionId === itemPositionId)
			if (!item || !Array.isArray(item.items)) return

			const subItem = item.items.find(subItem =>
				subItem.itemPositionId === subItemPositionId,
			)

			if (!subItem || subItem.status === 'CANCELED') return

			subItem.cancellationMetaData = { ...cancelSubItem }

			if (subItem.status !== 'CANCELED') {
				subItem.status = 'PREPARED_TO_CANCEL'
			}
		})

		return copyOrders
	},
)

export const selectCurrentOrdersInTableCart = createSelector(
	selectRegisteredOrdersWithCancellationData,
	selectNewOrder,
	(registeredOrdersWithCancellationData, newOrder) => {
		return newOrder ? [...registeredOrdersWithCancellationData, newOrder] : registeredOrdersWithCancellationData
	},
)

export const selectHasOnlyCanceledItemsInRegisteredOrders = createSelector(
	selectRegisteredOrdersWithCancellationData,
	(registeredOrdersWithCancellationData) => {
		return registeredOrdersWithCancellationData?.some(order => order?.items?.length > 0) &&
			registeredOrdersWithCancellationData?.every(order => order?.items?.every(item => ['CANCELED', 'PREPARED_TO_CANCEL'].includes(item?.status)))
	},
)

export const selectItemCancellationMetaData = createSelector(
	selectRegisteredOrders,
	selectRegisteredOrdersWithCancellationData,
	selectCancellationDetailsItem,
	(registeredOrders, ordersWithCancellationData, cancellationDetailsItem) => {
		if (!cancellationDetailsItem) return null

		const orders = cancellationDetailsItem?.status === 'CANCELED'
			? registeredOrders
			: ordersWithCancellationData

		if (cancellationDetailsItem?.itemType === 'ITEM') {
			// Find the item in orders
			for (const order of orders) {
				const item = order.items?.find(item => item.itemPositionId === cancellationDetailsItem.id)
				if (item) {
					return item.cancellationMetaData
				}
			}
		} else {
			// Search for subitem (add-on) in items
			for (const order of orders) {
				for (const item of order.items || []) {
					if (Array.isArray(item.items)) {
						const subItem = item.items.find(subItem => subItem.itemPositionId === cancellationDetailsItem.id)
						if (subItem) {
							return subItem.cancellationMetaData
						}
					}
				}
			}
		}

		return null
	},
)

export const selectGiftCouponInTableCart = createSelector(
	selectCurrentOrdersInTableCart,
	(orders) => {
		if (orders?.length) {
			return !!orders[0].items?.find(item => item.type === 'GIFT_COUPON') || null
		}
		return null
	},
)

export const selectDiscountOnTable = (orders, hasGiftCoupon) => createSelector(
	selectCartDiscountValue(hasGiftCoupon),
	(discount) => {
		const ordersTotalWithDiscount = orders?.reduce((res, el) => res + el.items.reduce((tp, i) => tp + calcCartItemTotalPrice(i, true), 0), 0)
		return getPercentDiscountInCurrency(discount, ordersTotalWithDiscount)
	},
)

export const selectTotalPriceNewOrder = createSelector(
	selectNewOrder,
	selectGiftCouponInTableCart,
	selectCartDiscount,
	(newOrder, hasGiftCoupon, discount) => {
		const itemsTotalWithDiscount = newOrder?.items.reduce((tp, i) => tp + calcCartItemTotalPrice(i, true), 0)
		const discountValue = hasGiftCoupon ? 0 : discount
		const discountInCurrencyNewOrder = getPercentDiscountInCurrency(discountValue, itemsTotalWithDiscount)
		return roundPrice(newOrder?.totalPrice - discountInCurrencyNewOrder)
	},
)

export const selectTotalPriceWithCancellationDataOrder = createSelector(
	selectRegisteredOrdersWithCancellationData,
	selectGiftCouponInTableCart,
	selectCartDiscount,
	(orders, hasGiftCoupon, discount) => {
		const totalPrice = orders.reduce((total, order) => {
			if (!order || !order.items) return total
			const copyOrder = { ...order }
			const orderTotalPrice = copyOrder.items.filter(item => !['CANCELED', 'PREPARED_TO_CANCEL'].includes(item.status)).reduce((it, i) => it + calcItemTotalPrice(i), 0)

			const ordersTotalWithDiscount = copyOrder.items
				.filter(item => !['CANCELED', 'PREPARED_TO_CANCEL'].includes(item.status))
				.reduce((tp, i) => tp + calcCartItemTotalPrice(i, true), 0)

			const discountValue = hasGiftCoupon ? 0 : discount
			const discountInCurrency = getPercentDiscountInCurrency(discountValue, ordersTotalWithDiscount)

			return total + (orderTotalPrice - discountInCurrency)
		}, 0)

		return roundPrice(totalPrice)
	},
)

export const selectTotalPriceOnTable = createSelector(
	selectRegisteredOrdersWithCancellationData,
	selectTotalPriceWithCancellationDataOrder,
	selectTotalPriceNewOrder,
	selectCancelItems,
	selectCancelSubItems,
	(registeredOrdersWithCancellationData, totalPriceWithCancellationDataOrder, totalPriceNewOrder, cancelItems, cancelSubItems) => {
		const totalPriceRegisteredOrders = (cancelItems?.length || cancelSubItems?.length) ? totalPriceWithCancellationDataOrder : registeredOrdersWithCancellationData?.reduce((tp, o) => tp + o.totalPrice, 0)

		if (totalPriceNewOrder > 0) {
			return totalPriceRegisteredOrders + totalPriceNewOrder
		}
		return totalPriceRegisteredOrders
	},
)

const selectModalLoginUserName = store => store.modalLogin.username

export const selectProcessedBy = createSelector(
	selectModalLoginUserName,
	selectUsername,
	(modalLoginUserName, userName) => {
		return modalLoginUserName ? modalLoginUserName : userName
	},
)

const selectDishCoursesEnabled = store => store.restaurantSettings.dishCourses?.enabled
const selectDishCourseIndex = state => state.tableCart.selectedDishCourseIndex

export const selectSelectDishCourseIndex = createSelector(
	selectDishCoursesEnabled,
	selectDishCourseIndex,
	(dishCoursesEnabled, dishCourseIndex) => {
		return dishCoursesEnabled ? { index: dishCourseIndex } : null
	},
)

const selectPresetDiscounts = store => store.restaurantSettings.presetDiscounts
export const selectSelectedPresetDiscount = (presetDiscountId) => createSelector(
	selectPresetDiscounts,
	(presetDiscounts) => {
		return presetDiscounts.find(discount => discount.id === presetDiscountId) ?? null
	},
)

export const selectExtraDiscountItemOnTable = (presetDiscountId, totalPrice, customerDiscount, customerDiscountType) => createSelector(
	selectSelectedPresetDiscount(presetDiscountId),
	(selectedPresetDiscount) => {
		if (selectedPresetDiscount) {
			const presetDiscountPrice = calculateTotalDiscount(selectedPresetDiscount.discount, 'PERCENT', totalPrice)
			const presetDiscountName = `${selectedPresetDiscount.name} ${selectedPresetDiscount.discount}%`
			return convert2ExtraDiscountItem(presetDiscountId, presetDiscountName, presetDiscountPrice)
		} else {
			const customExtraDiscount = calculateTotalDiscount(customerDiscount, customerDiscountType, totalPrice)
			const customExtraDiscountName = 'Extra Rabatt'
			return convert2ExtraDiscountItem(null, customExtraDiscountName, customExtraDiscount)
		}
	},
)

const selectTablesWaiterCalls = state => state.restaurant.tablesWaiterCalls
const selectCompanyIndex = state => state.login?.companyIndex

export const selectActiveTablesWaiterCalls = createSelector(
	selectTablesWaiterCalls,
	selectTimerCounter,
	selectCompanyIndex,
	(tablesWaiterCalls, counter, companyIndex) => {
		logger.info(`Tables waiter calls companyIndex=${companyIndex}`, JSON.stringify(tablesWaiterCalls))
		return tablesWaiterCalls?.filter(item => {
			const currentTime = new Date()
			const waiterCallTimeout = addMinutes(new Date(item?.callTime), 15)
			logger.info(`Table waiter calls time companyIndex=${companyIndex}`, currentTime, waiterCallTimeout, currentTime.getTime() < waiterCallTimeout.getTime())
			return currentTime.getTime() < waiterCallTimeout.getTime()
		})
	},
)

const selectKitchenConfig = store => store.kitchenMonitor.config
export const selectRestaurantAreas = store => store.restaurant.areas
export const selectRestaurantAreaId = store => store.restaurant.areaId

export const selectIsSelectedItemsKitchenConfigMode = createSelector(
	selectKitchenConfig,
	(kitchenConfig) => {
		return kitchenConfig?.mode === 'SELECTED_ITEMS'
	},
)

export const selectRestaurantSelectedArea = createSelector(
	selectRestaurantAreas,
	selectRestaurantAreaId,
	(areas, areaId) => {
		return areas?.find(area => area.id === areaId) ?? null
	},
)

export const selectRestaurantPaidBeforeOrdersTable = store => store.restaurant.table?.payBeforeOrders || []

export const selectAllTableOrders = createSelector(
	selectRegisteredOrders,
	selectRegisteredSplits,
	selectRestaurantPaidBeforeOrdersTable,
	(orders = [], splits = [], paidBeforeOrders = []) => {
		return [...orders, ...splits, ...paidBeforeOrders]
	},
)

export const selectTableWaiterNotifiedOrders = createSelector(
	selectAllTableOrders,
	(orders) => {
		return orders?.reduce((acc, order) => {
			const waiterNotifiedItems = order?.items?.filter(item => item?.kitchenMeta?.notificationStatus === 'WAITER_NOTIFIED')
			if (waiterNotifiedItems.length) {
				const item = { orderId: order.id, items: waiterNotifiedItems }
				acc.push(item)
			}
			return acc
		}, [])
	},
)

export const selectIsExistPaidBeforeOrdersOnTable = createSelector(
	selectRestaurantPaidBeforeOrdersTable,
	(paidBeforeOrders) => {
		return !!paidBeforeOrders.length
	},
)
export const selectIsOnlyPaidBeforeOrdersOnTable = createSelector(
	selectRestaurantOrdersTable,
	selectRestaurantSplitsTable,
	selectIsExistPaidBeforeOrdersOnTable,
	(orders, splits, isExistPaidBeforeOrdersOnTable) => {
		return isExistPaidBeforeOrdersOnTable && !orders.length && !splits.length
	},
)

const selectValidatedFlyerCoupon = state => state.coupon?.validatedFlyerCoupon

export const selectMarketingCouponItemOnTable = (totalPrice, t) => createSelector(
	selectValidatedFlyerCoupon,
	selectFlyerCouponError(totalPrice, t),
	(validatedFlyerCoupon, isErrorCoupon) => {
		if (!validatedFlyerCoupon || isErrorCoupon) return null
		const flyerCouponDiscount = calculateTotalDiscount(validatedFlyerCoupon.discountData.discount, validatedFlyerCoupon.discountData.discountType, totalPrice)
		return convert2MarketingCouponItem(validatedFlyerCoupon, flyerCouponDiscount)
	},
)

export const selectRegisteredOrder = createSelector(
	selectRegisteredOrders,
	(orders) => {
		const items = orders?.flatMap(order => order?.items) || []
		const totalPrice = orders?.reduce((sum, order) => sum + order?.totalPrice, 0)
		const payments = [{ total: totalPrice }]
		return { items, payments }
	},
)

export const selectTablesNotifications = state => state.restaurant.tablesNotifications

export const selectTablesWaiterCallsNotifications = createSelector(
	selectTablesNotifications,
	(tablesNotifications) => {
		return tablesNotifications?.filter(item => item.type === 'GUEST_ASK_WAITER')
	},
)

export const selectTableWaiterCall = (table) => createSelector(
	selectTablesWaiterCallsNotifications,
	(tablesNotifications) => {
		return tablesNotifications?.find(item => item.publicTableId === table?.publicId) ?? null
	},
)

export const selectAreaWaiterCall = (area) => createSelector(
	selectTablesWaiterCallsNotifications,
	(tablesNotifications) => {
		return tablesNotifications?.find(item => item.areaId === area?.id) ?? null
	},
)

export const selectAreaHasWaiterNotifiedTable = (areaId) => createSelector(
	selectTablesWaiterCallsNotifications,
	(tablesNotifications) => {
		return tablesNotifications?.find(item => item.areaId === areaId) > 0
	},
)
